import { createSlice } from '@reduxjs/toolkit';
import { apiContainer } from '@vlabs/api-bindings';
import { getWsEventsQS, getEventsQS, getGeneralEventsQS } from '@vlabs/api-bindings/src/luna-client/events/mappers';
import { cloneDeep } from 'lodash';

import { selectAppConfig } from '@vlapps/point/src/app/store/selectors';

import { defaultFilters as defaultEventFilters } from '@vlabs/pages/events/defaultFilters';

import { getDefaultVideoAnalyticsFilters as defaultVAFilters } from './defaultVideoAnalyticsFilters';

export const prepareDate = (date) => Date.parse(date) || 0;

const store = createSlice({
  name: 'workspace',
  initialState: {
    data: [],
    notifyEvents: [],
    viewedNotifyIds: [],
    shownNotifyEvents: [],
    filters: {},
    pageSize: 30,
    pageSizeOptions: [30],
    allIds: [],
  },
  reducers: {
    setEvents(state, { payload: { events } }) {
      state.data = events;
      state.allIds = events?.map((event) => event.event_id || event.event?.event_id);
    },
    setParams(state, { payload: { filters } = {} }) {
      state.filters = filters;
    },
    setAsyncData: {
      reducer(state, { payload: event }) {
        const id = event.event_id || event.event.event_id;
        if (!state.allIds.includes(id)) {
          state.data = [event, ...state.data].slice(0, state.pageSize);
          state.allIds = [id, ...state.allIds].slice(0, state.pageSize);
          state.notifyEvents = [event, ...state.notifyEvents].slice(0, state.pageSize);
        }
      },
      prepare: (rawData) => ({ payload: typeof rawData === 'string' ? JSON.parse(rawData) : rawData }),
    },
    setNotifyEvents(state, { payload: { events } }) {
      const viewedNotify = [...state.viewedNotifyIds];
      state.notifyEvents = events.filter((event) => {
        const id = event?.event_id || event?.event?.event_id;
        return !viewedNotify.includes(id);
      });
    },
    setViewedNotifyIds(state, { payload: { id } }) {
      const notifyEvents = [...state.notifyEvents];
      const viewedNotify = [...state.viewedNotifyIds];
      const allIds = [...state.allIds];
      state.viewedNotifyIds = [
        ...viewedNotify.filter((el) => allIds.includes(el)),
        id,
      ];
      state.notifyEvents = [
        ...notifyEvents.filter((event) => (event.event_id || event.event.event_id) !== id),
      ];
    },
    setShownNotifyIds(state, { payload: { id } }) {
      const allIds = [...state.allIds];
      const shownNotify = [...state.shownNotifyEvents];
      state.shownNotifyEvents = [
        ...shownNotify.filter((el) => allIds.includes(el)),
        id,
      ];
    },
  },
});

export default store.reducer;

export const {
  setAsyncData,
  setEvents,
  setParams,
  setNotifyEvents,
  setViewedNotifyIds,
  setShownNotifyIds,
} = store.actions;

export const disconnectAsyncEvents = async () => {
  await apiContainer.lunaClient.ws.disconnect();
  await apiContainer.lunaClient.generalWs.disconnectAll();
};

export const connectLpAsyncEvents = (params = {}) => async (dispatch, getState) => {
  await apiContainer.lunaClient.ws.disconnect();

  const { workspace: { filters } } = getState();
  const isEnabledDetectionAndMatchEvents = cloneDeep(filters.isEnabledDetectionAndMatchEvents
    ?? defaultEventFilters.isEnabledDetectionAndMatchEvents);

  if (isEnabledDetectionAndMatchEvents) {
    apiContainer.lunaClient.ws.connect(params, null, { startClosed: false });
    apiContainer.lunaClient.ws.onmessage = async (event) => {
      dispatch(setAsyncData(event));
    };
  }
};

export const connectVideoAnalyticsAsyncEvents = (params) => async (dispatch) => {
  const paramsToUse = params ?? defaultVAFilters().videoAnalytics;
  apiContainer.lunaClient.generalWs.connect(cloneDeep(paramsToUse));

  apiContainer.lunaClient.generalWs.onmessage = async (event) => {
    dispatch(setAsyncData(event));
  };
};

export const connectAsyncEvents = () => async (dispatch) => {
  dispatch(connectLpAsyncEvents());
  dispatch(connectVideoAnalyticsAsyncEvents());
};

export const fetchLatestEvents = async (dispatch, getState) => {
  const state = getState();
  const appConfig = selectAppConfig(state);
  const isNotificationsEnabled = appConfig?.uiNotificationsEnabled !== false;

  const { workspace: { pageSize, filters } } = state;
  const { app: { features: { components: { video_analytics } } } } = state;

  const videoAnalyticsFilters = cloneDeep(filters.videoAnalytics ?? defaultVAFilters(video_analytics).videoAnalytics);
  const isEnabledDetectionAndMatchEvents = filters.isEnabledDetectionAndMatchEvents
    ?? defaultEventFilters.isEnabledDetectionAndMatchEvents;

  const generalEventsRequests = [];

  Object.entries(videoAnalyticsFilters).forEach(([event_type, { isEnabled, filters: analyticFilters }]) => {
    if (isEnabled) {
      const requestFilters = {
        ...analyticFilters,
        event_type,
        page: 1,
        page_size: pageSize,
      };

      const generalEventRequest = apiContainer.lunaClient.generalEvents
        .getAll(getGeneralEventsQS(requestFilters));

      generalEventsRequests.push(generalEventRequest);
    }
  });

  try {
    let sortedEventRoll = [];

    if (isEnabledDetectionAndMatchEvents) {
      // проверяем фильтр по спискам, если их больше одного - делаем по запросу для каждого списка, сортируем, выбираем pageSize событий
      const topMatchingCandidatesLabelFilter = filters?.top_matching_candidates_label;

      if (Array.isArray(topMatchingCandidatesLabelFilter) && topMatchingCandidatesLabelFilter.length > 1) {
        const filtersArray = topMatchingCandidatesLabelFilter.map((listId) => {
          return getEventsQS({
            ...filters, top_matching_candidates_label: listId, page: 1, page_size: pageSize,
          });
        });
        const promisesArray = filtersArray.map((f) => apiContainer.lunaClient.events.getAll(f));
        const flatArrayOfEvents = (await Promise.all(promisesArray)).flat();
        const uniqueEvents = Array.from(new Map(flatArrayOfEvents.map((e) => [e.event_id, e])).values());
        const result = uniqueEvents.sort((a, b) => Date.parse(b.create_time) - Date.parse(a.create_time)).slice(0, pageSize);
        sortedEventRoll = result;
      } else {
        const lpEvents = await apiContainer.lunaClient.events
          .getAll(getEventsQS({ ...filters, page: 1, page_size: pageSize }));
        sortedEventRoll = lpEvents;
      }
    }

    if (generalEventsRequests.length) {
      const generalEventsResponse = await Promise.allSettled(generalEventsRequests);

      const generalEventsData = generalEventsResponse
        .filter(({ status }) => status === 'fulfilled')
        .flatMap(({ value }) => value);
      sortedEventRoll = sortedEventRoll
        .concat(generalEventsData)
        .sort((a, b) => prepareDate(b.create_time) - prepareDate(a.create_time))
        .slice(0, pageSize);
    }

    dispatch(setEvents({ events: sortedEventRoll }));
    if (isNotificationsEnabled) {
      dispatch(setNotifyEvents({ events: sortedEventRoll }));
    }
  } catch (error) {
    dispatch(setEvents({ events: [] }));
    dispatch(setNotifyEvents({ events: [] }));
    throw error;
  }
};

export const resetFilters = (dispatch) => {
  dispatch(setParams({ filters: {} }));
};

export const updateFilters = (params) => async (dispatch) => {
  const filters = cloneDeep(params.filters);
  const asyncFilters = getWsEventsQS(filters);

  dispatch(setParams({ filters }));
  dispatch(fetchLatestEvents);
  dispatch(connectLpAsyncEvents(asyncFilters));
  dispatch(connectVideoAnalyticsAsyncEvents(filters.videoAnalytics));
};
